import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useScraperContext } from '../contexts/ScraperContext';

const DraggableContainer = styled.div<{ $isDragging: boolean; $x: number; $y: number }>`
  position: fixed;
  width: 120px;
  height: 120px;
  z-index: 10000;
  cursor: ${props => props.$isDragging ? 'grabbing' : 'grab'};
  transform: translate(${props => props.$x}px, ${props => props.$y}px);
  transition: ${props => props.$isDragging ? 'none' : 'transform 0.2s ease'};
  user-select: none;
`;

const CircleContainer = styled.div<{ $isRunning: boolean }>`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: ${props => props.$isRunning ? 
    'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)' : 
    'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)'
  };
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  border: 3px solid rgba(255, 255, 255, 0.2);
  position: relative;
  overflow: hidden;
`;

const InnerCircle = styled.div<{ $isRunning: boolean }>`
  width: 90px;
  height: 90px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const StatusIcon = styled.div<{ $isRunning: boolean }>`
  font-size: 24px;
  color: white;
  font-weight: bold;
  
  ${props => props.$isRunning && `
    animation: pulse 2s infinite;
    
    @keyframes pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.7; }
    }
  `}
`;

const ProgressText = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 600;
  text-align: center;
  line-height: 1.2;
`;

const ProgressRing = styled.div<{ $progress: number; $isRunning: boolean }>`
  position: absolute;
  top: -3px;
  left: -3px;
  width: 126px;
  height: 126px;
  border-radius: 50%;
  background: conic-gradient(
    from 0deg,
    ${props => props.$isRunning ? '#10b981' : 'transparent'} 0deg,
    ${props => props.$isRunning ? '#10b981' : 'transparent'} ${props => props.$progress * 3.6}deg,
    transparent ${props => props.$progress * 3.6}deg,
    transparent 360deg
  );
  opacity: ${props => props.$isRunning ? 0.8 : 0};
  transition: opacity 0.3s ease;
`;

interface GlobalDraggableScraperStatusProps {}

const GlobalDraggableScraperStatus: React.FC<GlobalDraggableScraperStatusProps> = () => {
  const { scraperStatus, isScraperHidden, handleReopenModal } = useScraperContext();
  const [position, setPosition] = useState({ x: window.innerWidth - 150, y: 100 });
  const [isDragging, setIsDragging] = useState(false);
  const [hasDragged, setHasDragged] = useState(false);
  const dragRef = useRef<{ startX: number; startY: number; startPosX: number; startPosY: number } | null>(null);

  // Debug logging
  console.log('🔍 GlobalDraggableScraperStatus: Render check');
  console.log('🔍 GlobalDraggableScraperStatus: scraperStatus:', scraperStatus);
  console.log('🔍 GlobalDraggableScraperStatus: isScraperHidden:', isScraperHidden);
  console.log('🔍 GlobalDraggableScraperStatus: scraperStatus.isRunning:', scraperStatus.isRunning);
  console.log('🔍 Should render?', scraperStatus.isRunning || isScraperHidden);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    setHasDragged(false);
    
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startPosX: position.x,
      startPosY: position.y,
    };
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging || !dragRef.current) return;

    const deltaX = e.clientX - dragRef.current.startX;
    const deltaY = e.clientY - dragRef.current.startY;
    
    // Mark as dragged if moved more than 5 pixels
    if (!hasDragged && (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5)) {
      setHasDragged(true);
    }

    const newX = Math.max(0, Math.min(window.innerWidth - 120, dragRef.current.startPosX + deltaX));
    const newY = Math.max(0, Math.min(window.innerHeight - 120, dragRef.current.startPosY + deltaY));

    setPosition({ x: newX, y: newY });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    
    // Only trigger reopen if it wasn't a drag
    if (!hasDragged) {
      handleReopenModal();
    }
    
    dragRef.current = null;
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, hasDragged]);

  const progress = scraperStatus.totalCount > 0 ? (scraperStatus.processedCount / scraperStatus.totalCount) * 100 : 0;

  // Only render if scraper is running AND modal is hidden
  if (!scraperStatus.isRunning || !isScraperHidden) {
    console.log('🔍 GlobalDraggableScraperStatus: Returning null - conditions not met');
    console.log('🔍 - scraperStatus.isRunning:', scraperStatus.isRunning);
    console.log('🔍 - isScraperHidden:', isScraperHidden);
    return null;
  }

  console.log('🔍 GlobalDraggableScraperStatus: Rendering component');
  console.log('🔍 GlobalDraggableScraperStatus: Position:', position);
  console.log('🔍 GlobalDraggableScraperStatus: Window dimensions:', { width: window.innerWidth, height: window.innerHeight });

  return (
    <DraggableContainer
      ref={ref => ref && ref.addEventListener('dragstart', e => e.preventDefault())}
      $isDragging={isDragging}
      $x={position.x}
      $y={position.y}
      onMouseDown={handleMouseDown}
      style={{ 
        backgroundColor: 'red', // Temporary debug styling
        border: '5px solid yellow' // Temporary debug styling
      }}
    >
      <CircleContainer $isRunning={scraperStatus.isRunning}>
        <ProgressRing $progress={progress} $isRunning={scraperStatus.isRunning} />
        <InnerCircle $isRunning={scraperStatus.isRunning}>
          <StatusIcon $isRunning={scraperStatus.isRunning}>
            {scraperStatus.isRunning ? '⚡' : '⏸️'}
          </StatusIcon>
          <ProgressText>
            {scraperStatus.isRunning ? (
              <>
                {scraperStatus.processedCount}/{scraperStatus.totalCount}
                <br />
                {Math.round(progress)}%
              </>
            ) : (
              'In Pausa'
            )}
          </ProgressText>
        </InnerCircle>
      </CircleContainer>
    </DraggableContainer>
  );
};

export default GlobalDraggableScraperStatus;